import React, { useState } from 'react';
import { Button, Col, FormLabel, FormControl, Modal, Row } from 'react-bootstrap';
import Main from '../styles/Main/Main';
import FormButtonGroup from '@/app/components/styles/FormButtonGroup';
import LaddaButton from '@/app/components/LaddaButton';
import useSendMassTextingMutation from '@/app/hooks/useSendMassTextingMutation';
import './PatientMessagingStyles.scss';

import SearchSubscribedPatients from './SearchSubscribedPatients';
import MessagePreview from 'App/components/Sms/MessagePreview';
import PageSpinner from 'App/components/PageSpinner';
import useCreateMesssagePreview from 'App/hooks/useCreateMesssagePreview';

const propTypes = {};
const defaultProps = {};

function MassTexting() {
    const [message, setMessageValue] = useState('');
    const [show, setShow] = useState(false);
    const [patientArr, setPatientArr] = useState([]);
    const { mutateAsync, isLoading } = useSendMassTextingMutation();

    const preview = useCreateMesssagePreview(message);

    const cancel = () => {
        setShow(false);
    };

    const reviewSubmit = () => {
        if (message.length > 14 && message.length <= preview.maxCount) {
            setShow(true);
        }
    };

    const send = async () => {
        await mutateAsync({ message: preview.messageWithoutBranchPhone, selectedPatients: patientArr });
        setMessageValue('');
        cancel();
    };

    if (preview.isLoading) {
        return <PageSpinner />;
    }

    return (
        <Main fluid className="mass-texting">
            <h1>Send Mass Text</h1>
            <Row>
                <Col md={12}>
                    <p>
                        Use this form to send mass texts to patients opted-in to patient messaging. The character limit
                        for this text includes the branch name and opt out verbiage. These items are required and cannot
                        be removed.
                    </p>
                </Col>
            </Row>

            <Row className="disclaimer">
                <Col md={12}>
                    <span>
                        Disclaimer: SMS is unsecure and unencrypted. DO NOT SEND PHI. Do not include patient-specific
                        information. These texts should be used for informational purposes only (example use cases:
                        pharmacy closures, holidays, weather events).
                    </span>
                </Col>
            </Row>

            <div className="form">
                <Row>
                    <Col>
                        <FormLabel htmlFor="mass-texting-message">
                            <strong>
                                Your Message <span className="required">*</span>
                            </strong>
                        </FormLabel>
                        <span className="float-right">{`${message.length}/${preview.maxCount}`}</span>
                        <FormControl
                            value={message}
                            id="mass-texting-message"
                            onChange={(e) => setMessageValue(e.target.value)}
                            as="textarea"
                            rows={4}
                            placeholder="Enter your message"
                            maxLength={preview.maxCount}
                            role="textbox"
                            aria-label="Enter your message"
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <MessagePreview message={preview.messageWithoutBranchPhone} />
                    </Col>
                </Row>

                <div>
                    <FormButtonGroup>
                        <Button
                            disabled={message.length < 15}
                            variant="primary"
                            onClick={reviewSubmit}
                            aria-label="submit-massText-message"
                        >
                            Send
                        </Button>
                    </FormButtonGroup>
                </div>
                <Row>
                    <Col md={2} className="selected-patients-cnt-text">
                        <span>
                            <strong>Selected Patients:</strong>
                        </span>
                    </Col>
                    <Col>
                        <i className="fas fa-fw fa-check text-success" />
                        <span>
                            Sending to
                            <strong> {patientArr.length > 0 ? patientArr.length : 'all'} </strong>
                            patients.
                        </span>
                    </Col>
                </Row>
            </div>

            <Row>
                <Col md={12}>
                    <SearchSubscribedPatients
                        addPatients={(filteredPatientArr) => {
                            const patientSet = new Set([...patientArr, ...filteredPatientArr]);
                            setPatientArr(Array.from(patientSet));
                        }}
                    />
                </Col>
            </Row>

            <Modal show={show} backdrop="static">
                <Modal.Header closeButton={false}>
                    <Modal.Title role="heading" className="header">
                        Send Mass Text
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <MessagePreview message={preview.messageWithoutBranchPhone} />

                    <div className="modal-body-disclaimer">
                        <p>
                            Are you sure you want to send this mass text to
                            <strong>
                                {' '}
                                {patientArr.length > 0 ? patientArr.length : 'all'}&nbsp; subscribed patients?
                            </strong>
                        </p>
                        <p>
                            <strong>This will send immediately.</strong>
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <FormButtonGroup>
                        <Button variant="outline-secondary" name="cancel" onClick={cancel} aria-label="Cancel Message">
                            Cancel
                        </Button>
                        <LaddaButton
                            variant="primary"
                            name="send"
                            onClick={send}
                            disabled={isLoading}
                            isLoading={isLoading}
                            aria-label="Send Message"
                        >
                            Send
                        </LaddaButton>
                    </FormButtonGroup>
                </Modal.Footer>
            </Modal>
        </Main>
    );
}

MassTexting.prototype = propTypes;
MassTexting.defaultProps = defaultProps;

export default MassTexting;
